import React, { useState } from 'react';
import styled from 'styled-components';
import { Typography, Colors, AnalyticsComponent } from '@inplayer-org/inplayer-ui';
import { IoIosCheckmarkCircleOutline } from 'react-icons/io';

// Types
import { City, Country, Region } from 'ducks/merchantDucks/restrictions/types';

// Components
import BoxItemContent from 'components/BoxItemContent';
import TableActionMenu from 'components/Table/TableActionMenu';
import Restricted from 'restrictions/roles/components/Restricted';
import CommonIcon from 'components/CommonIcon';
import { Item } from 'components/BoxItem';

const {
  ActionMenuContainer,
  ActionMenuContent,
  ActionMenuDropdown,
  ActionMenuDropdownItem,
  ActionMenuLink,
} = TableActionMenu;

const DefaultContainer = styled.div`
  margin: 8px 0;

  p {
    font-size: ${({ theme }) => theme.font.sizes.small};
    font-style: italic;
  }
`;

const InPlayerIconDone = styled(IoIosCheckmarkCircleOutline)`
  vertical-align: text-bottom;
  margin-right: 8px;
  color: ${Colors.green};
`;

const CountriesContainer = styled.div`
  color: ${({ theme }) => theme.palette.text.light};
  font-size: ${({ theme }) => theme.font.sizes.small};
`;

interface RestrictedCountry extends Omit<Country, 'country'> {
  name: string;
}

interface Props {
  boxItem: Item;
  handleDeleteModal: (e: any) => any;
  handleNew: (type: string, boxItem?: Item) => any;
  handleDefaultModal: (boxItem: Item) => any;
  handleDuplicate: (type: string, boxItem: Item) => any;
  isReadOnly?: boolean;
}

const GeoRestrictionsItem = ({
  boxItem,
  boxItem: { id, type, countries, regions, cities, global },
  handleDeleteModal,
  handleNew,
  handleDefaultModal,
  handleDuplicate,
  isReadOnly = false,
}: Props) => {
  const [isActive, setActive] = useState(true);
  const restrictionType = type === 'blacklist' ? 'Excludes' : 'Includes';

  const renderItem = (item: RestrictedCountry | Region | City, index: number) => (
    <span key={index}>{(index ? ', ' : '') + item.name}</span>
  );

  const renderRestrictedItems = (restrctedItems: (RestrictedCountry | Region | City)[], restrictedType: string) => {
    let restricted = 'country';
    switch (restrictedType) {
      case 'regions':
        restricted = 'region';
        break;
      case 'cities':
        restricted = 'city';
        break;
      default:
        break;
    }

    return (
      <>
        {restrctedItems.length <= 2 ? (
          restrctedItems.map(renderItem)
        ) : (
          <>
            {restrctedItems.slice(0, 2).map(renderItem)} and {restrctedItems.length - 2} more{' '}
            {restrctedItems.length - 2 === 1 ? `${restricted}.` : `${restrictedType}.`}
          </>
        )}
      </>
    );
  };

  // rename key prop country to name in order to be the same as the one from regions and cities
  const restrictedCountries = countries?.map(({ country, ...countryItem }) => ({
    name: country,
    ...countryItem,
  }));
  const showRestrictedCountries = restrictedCountries && restrictedCountries.length !== 0;
  const showRestrictedRegions = regions && regions.length !== 0;
  const showRestrictedCities = cities && cities.length !== 0;

  const renderGeoRestrictionFooter = () => {
    return (
      <>
        <DefaultContainer>
          {global && (
            <Typography variant="p">
              <InPlayerIconDone />
              Preset on new assets
            </Typography>
          )}
        </DefaultContainer>
        {restrictionType}
        {showRestrictedCountries && (
          <CountriesContainer>Countries: {renderRestrictedItems(restrictedCountries, 'countries')}</CountriesContainer>
        )}
        {showRestrictedRegions && (
          <CountriesContainer>Regions: {renderRestrictedItems(regions, 'regions')}</CountriesContainer>
        )}
        {showRestrictedCities && (
          <CountriesContainer>Cities: {renderRestrictedItems(cities, 'cities')}</CountriesContainer>
        )}
      </>
    );
  };

  const renderActionMenu = () => {
    return (
      <AnalyticsComponent>
        {({ pages, tracker, merchantId, ip }) => (
          <ActionMenuContainer
            onMouseDown={() => setActive(true)}
            onMouseOut={() => setActive(false)}
            onClick={() =>
              !isActive &&
              tracker.track({
                event: 'click',
                type: 'icon',
                tag: `kebab_${boxItem.id}`,
                pages,
                merchantId,
                ip,
              })
            }
          >
            <ActionMenuContent>
              <ActionMenuDropdown>
                <ActionMenuLink href="#/" onClick={handleNew && handleNew('edit', boxItem)}>
                  <ActionMenuDropdownItem
                    onClick={() =>
                      tracker.track({
                        event: 'click',
                        type: 'icon',
                        tag: `kebab_${boxItem.id}_edit`,
                        pages,
                        merchantId,
                        ip,
                      })
                    }
                  >
                    {!isReadOnly ? 'Edit' : 'View'}
                  </ActionMenuDropdownItem>
                </ActionMenuLink>
                <Restricted isRestricted={isReadOnly}>
                  <ActionMenuLink href="#/" onClick={handleDuplicate && handleDuplicate('duplicate', boxItem)}>
                    <ActionMenuDropdownItem
                      onClick={() => {
                        tracker.track({
                          event: 'click',
                          type: 'icon',
                          tag: `kebab_${boxItem.id}_duplicate`,
                          pages,
                          merchantId,
                          ip,
                        });
                      }}
                    >
                      Duplicate
                    </ActionMenuDropdownItem>
                  </ActionMenuLink>
                  <ActionMenuLink href="#/" onClick={handleDefaultModal && handleDefaultModal(boxItem)}>
                    <ActionMenuDropdownItem
                      onClick={() => {
                        tracker.track({
                          event: 'click',
                          type: 'icon',
                          tag: `kebab_${boxItem.id}_${global ? 'remove' : 'make'}_default`,
                          pages,
                          merchantId,
                          ip,
                        });
                      }}
                    >{`${global ? 'Remove' : 'Make'} default`}</ActionMenuDropdownItem>
                  </ActionMenuLink>
                  <ActionMenuLink href="#/" onClick={handleDeleteModal && handleDeleteModal(id)}>
                    <ActionMenuDropdownItem
                      onClick={() => {
                        tracker.track({
                          event: 'click',
                          type: 'icon',
                          tag: `kebab_${boxItem.id}_delete`,
                          pages,
                          merchantId,
                          ip,
                        });
                      }}
                    >
                      Delete
                    </ActionMenuDropdownItem>
                  </ActionMenuLink>
                </Restricted>
              </ActionMenuDropdown>
            </ActionMenuContent>
            <CommonIcon hoverStatus="more" name="more" />
          </ActionMenuContainer>
        )}
      </AnalyticsComponent>
    );
  };

  const showRestrictionFooter = countries || regions || cities;

  return (
    <BoxItemContent boxItem={boxItem} renderActionMenu={renderActionMenu}>
      {showRestrictionFooter && renderGeoRestrictionFooter()}
    </BoxItemContent>
  );
};

export default GeoRestrictionsItem;
